import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useUserPrint } from '@app/hooks';
import type { MultiSelectValue } from '@app/shared/components/MultiSelect/types';

import {
  DEFAULT_LEADS_REGISTRY_SEARCH_TYPE,
  getDefaultLeadsRegistryFilters,
  LEADS_REGISTRY_NO_RESPONSIBLE_OPTION_ID,
  LEADS_REGISTRY_SEARCH_TYPE_OPTIONS,
} from '../model/constants';
import {
  getLeadsPeriodPresetById,
  resolveLeadsPeriodPresetRange,
} from '../model/leadsDatePresets';
import { readLocalStorageItem, writeLocalStorageItem } from '../utils';

import type {
  LeadsPeriodPresetId,
  LeadsRegistryCheckboxFilterKey,
  LeadsRegistryChatScope,
  LeadsRegistryDateRange,
  LeadsRegistryFilterScope,
  LeadsRegistryFilters,
  LeadsRegistryPeriodType,
  LeadsRegistrySearchType,
} from '../model/types';

// Ordinary registry filter state, owned by Leads.
// It is shared between the header (filter controls) and the page body (search
// hook), which render in separate subtrees under the page shell. A module-level
// external store is used for the same reason as `useLeadsRegistrySettings`:
// the two consumers are siblings, so local component state cannot be shared.
const FILTER_STORAGE_KEYS: Record<LeadsRegistryFilterScope, string> = {
  registry: 'leads-v2:registry:filters',
  funnels: 'leads-v2:funnel:filters',
};

type SerializedLeadsRegistryFilters = Omit<
  LeadsRegistryFilters,
  'dates' | 'datePreset'
> & {
  dates: [string | null, string | null];
  // Absent in values stored before dynamic presets existed.
  datePreset?: LeadsPeriodPresetId | null;
};

const serializeFilters = (
  filters: LeadsRegistryFilters
): SerializedLeadsRegistryFilters => ({
  ...filters,
  // A preset period is persisted as the preset id only, so it is re-resolved
  // relative to the current day on every restore instead of being frozen to
  // absolute dates. Manual ranges are the opposite: fixed by design.
  dates: filters.datePreset
    ? [null, null]
    : (filters.dates.map((date) =>
        date ? date.toISOString() : null
      ) as SerializedLeadsRegistryFilters['dates']),
});

const deserializeFilters = (
  value: SerializedLeadsRegistryFilters
): LeadsRegistryFilters => {
  const isValidSearchType = LEADS_REGISTRY_SEARCH_TYPE_OPTIONS.some(
    (option) => option.value === value.searchType
  );
  // Stored preset id wins over dates; an unknown/absent id means either a
  // manual fixed range (explicit dates present) or no period at all (the
  // period is optional — an empty range is a legitimate, persisted choice).
  const storedPreset = value.datePreset
    ? getLeadsPeriodPresetById(value.datePreset)
    : null;
  const datePreset = storedPreset ? storedPreset.id : null;

  return {
    ...getDefaultLeadsRegistryFilters(),
    ...value,
    searchType: isValidSearchType
      ? value.searchType
      : DEFAULT_LEADS_REGISTRY_SEARCH_TYPE,
    salesFunnelId:
      typeof value.salesFunnelId === 'number' ? value.salesFunnelId : null,
    datePreset,
    dates: datePreset
      ? resolveLeadsPeriodPresetRange(datePreset)
      : [
          value.dates[0] ? dayjs(value.dates[0]) : null,
          value.dates[1] ? dayjs(value.dates[1]) : null,
        ],
  };
};

// Одноразовый сброс способа поиска на «По телефону» (task/11141). Смена
// дефолта сама по себе не трогает тех, кто уже открывал реестр: их выбор
// лежит в localStorage и восстанавливается. Поэтому при первом заходе после
// выката подменяем searchType в обоих сохранённых наборах и запоминаем, что
// сброс сделан, — дальше выбор пользователя снова сохраняется как обычно.
const SEARCH_TYPE_RESET_FLAG_KEY = 'leads-v2:search-type-reset:v1';

const resetStoredSearchTypeOnce = (): void => {
  if (readLocalStorageItem(SEARCH_TYPE_RESET_FLAG_KEY)) return;

  Object.values(FILTER_STORAGE_KEYS).forEach((storageKey) => {
    const rawValue = readLocalStorageItem(storageKey);
    if (!rawValue) return;

    try {
      const storedFilters = JSON.parse(
        rawValue
      ) as SerializedLeadsRegistryFilters;

      writeLocalStorageItem(
        storageKey,
        JSON.stringify({
          ...storedFilters,
          searchType: DEFAULT_LEADS_REGISTRY_SEARCH_TYPE,
        })
      );
    } catch {
      // Битое значение читать не нужно: readStoredFilters ниже всё равно
      // отдаст фильтры по умолчанию, уже с «По телефону».
    }
  });

  writeLocalStorageItem(SEARCH_TYPE_RESET_FLAG_KEY, 'done');
};

const readStoredFilters = (
  scope: LeadsRegistryFilterScope
): LeadsRegistryFilters => {
  try {
    const rawValue = readLocalStorageItem(FILTER_STORAGE_KEYS[scope]);
    if (!rawValue) return getDefaultLeadsRegistryFilters();

    return deserializeFilters(
      JSON.parse(rawValue) as SerializedLeadsRegistryFilters
    );
  } catch {
    return getDefaultLeadsRegistryFilters();
  }
};

const saveStoredFilters = (
  scope: LeadsRegistryFilterScope,
  filters: LeadsRegistryFilters
) => {
  writeLocalStorageItem(
    FILTER_STORAGE_KEYS[scope],
    JSON.stringify(serializeFilters(filters))
  );
};

resetStoredSearchTypeOnce();

let leadsRegistryFiltersState: Record<
  LeadsRegistryFilterScope,
  LeadsRegistryFilters
> = {
  registry: readStoredFilters('registry'),
  funnels: readStoredFilters('funnels'),
};

const listeners = new Set<() => void>();

const getSnapshot = () => leadsRegistryFiltersState;

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const setFiltersState = (
  scope: LeadsRegistryFilterScope,
  nextState: LeadsRegistryFilters
) => {
  leadsRegistryFiltersState = {
    ...leadsRegistryFiltersState,
    [scope]: nextState,
  };
  saveStoredFilters(scope, nextState);
  listeners.forEach((listener) => {
    listener();
  });
};

// Re-resolves preset-driven ranges against the current day. Called on day
// rollover (midnight timer, tab wake) so a tab that lived through midnight
// gets fresh dates without a reload; a no-op change is skipped to avoid
// useless re-renders and re-queries.
export const actualizeLeadsRegistryDynamicDates = () => {
  (
    Object.keys(leadsRegistryFiltersState) as LeadsRegistryFilterScope[]
  ).forEach((scope) => {
    const filters = leadsRegistryFiltersState[scope];
    if (!filters.datePreset) return;

    const resolved = resolveLeadsPeriodPresetRange(filters.datePreset);
    const toDay = (date: LeadsRegistryDateRange[number]) =>
      date?.format('YYYY-MM-DD') ?? null;
    const isSameRange =
      toDay(resolved[0]) === toDay(filters.dates[0]) &&
      toDay(resolved[1]) === toDay(filters.dates[1]);

    if (isSameRange) return;

    setFiltersState(scope, { ...filters, dates: resolved });
  });
};

const getChatScopeFromUsers = (
  users: string[],
  userId: number | null
): LeadsRegistryChatScope => {
  if (
    users.length === 1 &&
    users[0] === LEADS_REGISTRY_NO_RESPONSIBLE_OPTION_ID
  )
    return 'free';

  if (userId !== null && users.length === 1 && users[0] === String(userId))
    return 'mine';

  return 'all';
};

const sortNumberValues = (values: number[]) =>
  [...values].sort((a, b) => a - b);

const sortStringValues = (values: string[]) =>
  [...values].sort((a, b) => a.localeCompare(b));

const getComparableFilters = (filters: LeadsRegistryFilters) => ({
  ...filters,
  branches: sortStringValues(filters.branches),
  channels: sortStringValues(filters.channels),
  integrations: sortStringValues(filters.integrations),
  users: sortStringValues(filters.users),
  statuses: sortStringValues(filters.statuses),
  crmTaskStatuses: sortStringValues(filters.crmTaskStatuses),
  funnelStages: sortNumberValues(filters.funnelStages),
  dates: filters.dates.map((date) => date?.format('YYYY-MM-DD') ?? null),
  tags: {
    groups: filters.tags.groups.map((group) => ({
      ...group,
      selectedValues: sortStringValues(group.selectedValues),
    })),
  },
});

export const useLeadsRegistryFilters = () => {
  const scope: LeadsRegistryFilterScope = 'registry';
  return useLeadsRegistryScopedFilters(scope);
};

export const useLeadsRegistryScopedFilters = (
  scope: LeadsRegistryFilterScope
) => {
  const { params } = useUserPrint();
  const userId = params.UserId;
  const filtersState = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );
  const filters = filtersState[scope];
  const chatScope = getChatScopeFromUsers(filters.users, userId);
  const isFiltersResetDisabled = useMemo(
    () =>
      isEqual(
        getComparableFilters(filters),
        getComparableFilters(getDefaultLeadsRegistryFilters())
      ),
    [filters]
  );

  const searchChangeHandler = useCallback(
    (search: string) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, search });
    },
    [scope]
  );

  const searchTypeChangeHandler = useCallback(
    (searchType: LeadsRegistrySearchType) => {
      // Keep the typed term: switching «В сообщениях»/«В клиентах» re-runs
      // the same search in the new scope instead of clearing the input.
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, searchType });
    },
    [scope]
  );

  const checkboxFilterChangeHandler = useCallback(
    (key: LeadsRegistryCheckboxFilterKey, values: string[]) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, [key]: values });
    },
    [scope]
  );

  const chatScopeChangeHandler = useCallback(
    (nextChatScope: LeadsRegistryChatScope) => {
      const usersByChatScope: Record<LeadsRegistryChatScope, string[]> = {
        all: [],
        mine: userId !== null ? [String(userId)] : [],
        free: [LEADS_REGISTRY_NO_RESPONSIBLE_OPTION_ID],
      };

      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, {
        ...currentFilters,
        users: usersByChatScope[nextChatScope],
      });
    },
    [scope, userId]
  );

  const tagsChangeHandler = useCallback(
    (tags: MultiSelectValue) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, tags });
    },
    [scope]
  );

  const funnelStagesChangeHandler = useCallback(
    (funnelStages: number[]) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, funnelStages });
    },
    [scope]
  );

  const salesFunnelChangeHandler = useCallback(
    (salesFunnelId: number | null, funnelStages: number[] = []) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, {
        ...currentFilters,
        salesFunnelId,
        funnelStages,
      });
    },
    [scope]
  );

  const datesChangeHandler = useCallback(
    (dates: LeadsRegistryDateRange) => {
      // A range picked in the calendar is fixed by design: it clears the
      // active preset so the stored dates stop following the current day.
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, dates, datePreset: null });
    },
    [scope]
  );

  const datePresetChangeHandler = useCallback(
    (datePreset: LeadsPeriodPresetId) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, {
        ...currentFilters,
        datePreset,
        dates: resolveLeadsPeriodPresetRange(datePreset),
      });
    },
    [scope]
  );

  const periodTypeChangeHandler = useCallback(
    (periodType: LeadsRegistryPeriodType) => {
      const currentFilters = leadsRegistryFiltersState[scope];
      setFiltersState(scope, { ...currentFilters, periodType });
    },
    [scope]
  );

  const resetFiltersHandler = useCallback(() => {
    setFiltersState(scope, getDefaultLeadsRegistryFilters());
  }, [scope]);

  return {
    chatScope,
    filters,
    isFiltersResetDisabled,
    onSearchChange: searchChangeHandler,
    onSearchTypeChange: searchTypeChangeHandler,
    onCheckboxFilterChange: checkboxFilterChangeHandler,
    onChatScopeChange: chatScopeChangeHandler,
    onTagsChange: tagsChangeHandler,
    onFunnelStagesChange: funnelStagesChangeHandler,
    onSalesFunnelChange: salesFunnelChangeHandler,
    onDatesChange: datesChangeHandler,
    onDatePresetChange: datePresetChangeHandler,
    onPeriodTypeChange: periodTypeChangeHandler,
    onResetFilters: resetFiltersHandler,
  };
};
